import * as React from 'react';
import { visualize, story, snap } from 'storybook-snapper';
import { ActionsMenuLayout, ActionsMenuLayoutItemAlignment } from '..';
import { TPAComponentsProvider } from '../TPAComponentsConfig';
import { LikeHeart as Heart } from '@wix/wix-ui-icons-common/on-stage';
import { classes } from './ActionsMenuLayout.visual.st.css';
import { EditorShell, VisualAsync } from '../../test/visual/VisualAsync';
import {
  ActionsMenuLayoutDriver,
  actionsMenuLayoutDriverFactory,
} from './ActionsMenuLayout.driver';

function generateItems(props = {}) {
  const { subtitle, prefixIcon } = props as any;
  let counter = 0;

  return ['Item', 'Item', 'Divider', 'Item'].map((type, index) => {
    const Component = ActionsMenuLayout[type];
    const content = type === 'Item' ? ++counter : undefined;

    return (
      <Component
        key={index}
        disabled={index === 1}
        content={`Item ${content}`}
        subtitle={subtitle ? `Subtitle ${content}` : undefined}
        prefixIcon={prefixIcon ? <Heart /> : undefined}
        onClick={() => {}}
      />
    );
  });
}

function testSuite(alignment) {
  story(`Align ${alignment ? alignment : 'default'}`, () => {
    snap('several item', () => (
      <ActionsMenuLayout alignment={alignment}>
        {generateItems()}
      </ActionsMenuLayout>
    ));

    snap('with subtitle', () => (
      <ActionsMenuLayout alignment={alignment}>
        {generateItems({ subtitle: true })}
      </ActionsMenuLayout>
    ));

    snap('with icon', () => (
      <ActionsMenuLayout alignment={alignment}>
        {generateItems({ prefixIcon: true })}
      </ActionsMenuLayout>
    ));

    snap('with subtitle & icon', () => (
      <ActionsMenuLayout alignment={alignment}>
        {generateItems({ subtitle: true, prefixIcon: true })}
      </ActionsMenuLayout>
    ));

    snap('rtl', () => (
      <div dir="rtl">
        <ActionsMenuLayout alignment={alignment}>
          {generateItems({ subtitle: true, prefixIcon: true })}
        </ActionsMenuLayout>
      </div>
    ));

    snap('rtl', () => (
      <div dir="rtl">
        <ActionsMenuLayout alignment={alignment}>
          {generateItems({ subtitle: true, prefixIcon: true })}
        </ActionsMenuLayout>
      </div>
    ));

    snap('mobile', () => (
      <TPAComponentsProvider value={{ mobile: true }}>
        <ActionsMenuLayout alignment={alignment}>
          {generateItems({ subtitle: true, prefixIcon: true })}
        </ActionsMenuLayout>
      </TPAComponentsProvider>
    ));

    snap('mobile with focus', () => (
      <EditorShell>
        <TPAComponentsProvider value={{ mobile: true }}>
          <ActionsMenuLayout alignment={alignment} focusedIndex={1}>
            {generateItems({ subtitle: true, prefixIcon: true })}
          </ActionsMenuLayout>
        </TPAComponentsProvider>
      </EditorShell>
    ));

    snap('RTL', () => (
      <TPAComponentsProvider value={{ rtl: true }}>
        <ActionsMenuLayout alignment={alignment}>
          {generateItems({ subtitle: true, prefixIcon: true })}
        </ActionsMenuLayout>
      </TPAComponentsProvider>
    ));

    snap('should have focus on second item', () => (
      <EditorShell>
        <ActionsMenuLayout alignment={alignment} focusedIndex={1}>
          {generateItems()}
        </ActionsMenuLayout>
      </EditorShell>
    ));

    snap('with overrides', () => (
      <ActionsMenuLayout alignment={alignment} className={classes.styled}>
        {generateItems({ subtitle: true })}
      </ActionsMenuLayout>
    ));
  });
}

visualize('ActionsMenuLayout', () => {
  testSuite(undefined);

  Object.values(ActionsMenuLayoutItemAlignment).forEach(testSuite);
});

visualize('ActionsMenuLayout', () => {
  story('focus', () => {
    const its = [
      {
        it: 'no initial focus',
        props: {},
        action: () => Promise.resolve(),
      },
      {
        it: 'initial focus at 0',
        props: { focusedIndex: 0 },
        action: () => Promise.resolve(),
      },
      {
        it: 'initial focus at 1',
        props: { focusedIndex: 1 },
        action: () => Promise.resolve(),
      },
      {
        it: 'initial focus at 2',
        props: { focusedIndex: 2 },
        action: () => Promise.resolve(),
      },
      {
        it: 'disabled item focus',
        props: { focusedIndex: 0 },
        action: async (driver: ActionsMenuLayoutDriver) => {
          // TODO: JB: pressKey is not working as expected
          // await driver.layout().pressKey(KEYS.ArrowDown);
          (await driver.item('Item 2').getNative()).focus();
        },
      },
    ];
    its.forEach(({ it, action, props }) =>
      snap(it, (done) => (
        <VisualAsync
          driverFactory={actionsMenuLayoutDriverFactory}
          onDone={done}
          action={(driver) => action(driver)}
        >
          <ActionsMenuLayout {...props}>{generateItems()}</ActionsMenuLayout>
        </VisualAsync>
      )),
    );
  });

  story('with wst theme', () => {
    snap('default', () => {
      const style = {
        '--wst-primary-background-color-rgb': '0,155,255',
        '--wst-paragraph-2-color-rgb': '170,0,20',
        '--wst-shade-3-color-rgb': '0,255,20',
        '--wst-paragraph-2-font-variant': 'small-caps',
        '--wst-paragraph-2-font-style': 'italic',
        '--wst-paragraph-2-font-size': '22px',
        '--wst-paragraph-2-font-family': 'Libre Baskerville',
        '--wst-paragraph-2-font-weight': '900',
      } as React.CSSProperties;

      return (
          <div style={style}>
          <ActionsMenuLayout className={classes.root}>
            {generateItems({ subtitle: true })}
          </ActionsMenuLayout>
        </div>
      );
    });
  });
});
